import { useLocation, type Location } from 'react-router';
import { useSuspenseMaxAPIKeysQuery } from '#api/api-keys.queries.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { ButtonGroup, ButtonGroupItem } from '#components/patterns/button-group/ButtonGroup.tsx';
import { Snippet } from '#components/patterns/code/snippet/Snippet.tsx';
import { TableLayout } from '#components/patterns/layouts/table/TableLayout.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import type { AppState } from '#hooks/useAppLocation.ts';
import { APIKeysList } from '../list/api-keys-list/APIKeysList.tsx';
import { EmptyAPIKeys } from '../list/empty/EmptyAPIKeys.tsx';
import type { NewKeyState } from '../types.ts';

type LocationState =
  | (AppState & {
      newKey?: NewKeyState;
    })
  | undefined;

export function APIKeysPage() {
  const location = useLocation() as Location<LocationState>;
  const {
    data: { data: apiKeys },
    isFetching,
  } = useSuspenseMaxAPIKeysQuery();
  const { data: { ROLE_SCOPED_API_KEYS: hasRoleScopedKeys } = {} } = useSuspenseFeatureFlagsQuery();
  const newKey = location.state?.newKey;

  if (!apiKeys.length) {
    return <EmptyAPIKeys addAPIKeyHref="add" />;
  }

  return (
    <TableLayout
      headerClass="padb-0"
      header={
        <PageHeader
          className="mb-32"
          title="API keys"
          subtitle={
            newKey ?
              <>
                <p className="label">
                  Token <strong>{newKey.name}</strong> created. Copy and store it somewhere safe. It cannot be viewed
                  after leaving this page.
                </p>
                <Snippet variant="success" breakWord={true}>
                  {newKey.token}
                </Snippet>
              </>
            : null
          }
        >
          <ButtonGroup>
            <ButtonGroupItem>
              <LinkButton size="small" to="add">
                Add
              </LinkButton>
            </ButtonGroupItem>
          </ButtonGroup>
        </PageHeader>
      }
    >
      <APIKeysList className="mb-48" data={apiKeys} isFetching={isFetching} showRoleScope={hasRoleScopedKeys} />
    </TableLayout>
  );
}
